import React from "react";
import { AlertTriangle, ChevronRight, X } from "lucide-react";

export default function NudgeOverlay({ nudges, onDismiss }) {
  if (!nudges || nudges.length === 0) return null;

  return (
    <div className="fixed top-24 right-6 z-[60] flex flex-col gap-3 max-w-sm w-full pointer-events-none">
      <div className="flex flex-col gap-3 pointer-events-auto">
        {nudges.slice(0, 5).map((nudge) => (
          <div
            key={nudge.id}
            className={`
              relative group overflow-hidden bg-[#1a1d29] border border-gray-700 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.5)] 
              animate-slide-in-right hover:shadow-[0_10px_40px_rgba(0,0,0,0.6)] hover:border-gray-500 transition-all duration-300
            `}
          >
            {/* Diff Gradient Top Bar */}
            <div className={`h-1 w-full ${
              nudge.diff <= 5 ? "bg-red-500" : nudge.diff <= 10 ? "bg-orange-500" : "bg-yellow-500"
            }`} />

            <div className="p-3">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className={`p-1.5 rounded-lg ${
                    nudge.sideLabel === "BUY" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
                  }`}>
                    <AlertTriangle className="w-4 h-4" />
                  </span>
                  <div>
                    <h4 className="text-sm font-bold text-white leading-tight">{nudge.symbol}</h4>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">{nudge.strategy}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-black ${
                    nudge.sideLabel === "BUY" ? "bg-green-600 text-white" : "bg-red-600 text-white"
                  }`}>
                    {nudge.sideLabel}
                  </span>
                  {onDismiss && (
                    <button 
                      onClick={() => onDismiss(nudge.id)}
                      className="p-1 hover:bg-gray-800 rounded-md text-gray-500 hover:text-white transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-end mt-4">
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter">Gap to Trigger</span>
                  <span className={`text-lg font-black leading-none ${
                    nudge.diff <= 5 ? "text-red-500" : nudge.diff <= 10 ? "text-orange-500" : "text-yellow-500"
                  }`}>
                    ₹{nudge.diff.toFixed(2)}
                  </span>
                </div>
                
                <div className="flex flex-col items-end">
                  {nudge.baseQty !== "N/A" && (
                    <span className="text-[10px] text-gray-400">Target Qty: <span className="text-blue-400 font-bold">{nudge.baseQty}</span></span>
                  )}
                  <div className="mt-1 flex items-center gap-1.5 bg-[#0f1116] px-2 py-1 rounded border border-gray-800">
                    <span className="text-[10px] text-gray-500 font-bold">CLIENTS</span>
                    <span className="text-white font-black text-xs">{nudge.clientCount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Subtle pulsate for urgent nudges */}
            {nudge.diff <= 5 && (
              <div className="absolute inset-0 pointer-events-none border-2 border-red-500/30 rounded-xl animate-pulse" />
            )}
          </div>
        ))}
        {nudges.length > 5 && (
          <div className="text-right px-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase">+{nudges.length - 5} more nudges...</span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
}
